"use client";

import { cdnCommonPath } from "@/constants/cdnUrls";
import { useAuth } from "@/contexts/AuthContext";
import { useModal } from "@/contexts/ModalContext";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useKeyPressed } from "@/hooks/useKeyPressed";
import { useOnClickOutside } from "@/hooks/useOnClickOutside";
import { useToggle } from "@/hooks/useToggle";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { Link, usePathname } from "@/i18n/navigation";
import { savedCardService } from "@/services/accountServices/savedCardService";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { useLocale } from "next-intl";
import Image from "next/image";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "./ui/Modal";

const NotificationBadge = ({ show }) => {
  if (!show) return null;
  return (
    <div className="absolute -top-2 -right-2 inline-flex h-[18px] w-[18px] animate-bounce items-center justify-center rounded-full bg-red-500 text-center text-white transition-all duration-300 md:h-5 md:w-5">
      <span className="text-xs">1</span>
    </div>
  );
};

const InfoItem = ({ icon, title, description }) => (
  <div className="flex items-start justify-start gap-4">
    <span className="bg-background-global flex h-12 w-12 items-center justify-center rounded-lg">
      <Image
        src={`${cdnCommonPath}${icon}.svg`}
        alt={icon}
        width={32}
        height={32}
      />
    </span>
    <div className="flex flex-col text-left">
      <span className="font-medium">{title}</span>
      <span className="text-foreground-muted text-sm">{description}</span>
    </div>
  </div>
);

const CardItem = ({ card, t, close }) => (
  <Link
    href="/credit-card-points"
    onClick={close}
    className="border-primary-weak flex cursor-pointer items-center justify-between gap-4 rounded-lg border bg-white px-4 py-2 text-left transition-all duration-300 md:h-[76px] md:px-4"
  >
    <div className="flex items-center gap-2">
      <Image
        src={`${cdnCommonPath}${card.CardOrganizationType}.svg`}
        alt={card.CardProgram}
        width={40}
        height={27}
      />
      <div className="flex flex-col">
        <div className="w-25 truncate text-sm">{card.CardName}</div>
        <div className="inline-flex items-center gap-2 font-semibold">
          <span>{card.MaskedCardNumber}</span>
        </div>
        <span className="text-primary cursor-pointer md:hidden">
          {t("common.convert")}
        </span>
      </div>
    </div>
    <span className="text-primary hidden cursor-pointer md:block">
      {t("common.convert")}
    </span>
  </Link>
);

const EmptyState = ({ t, close }) => (
  <div className="flex flex-col gap-6">
    <div className="flex flex-col gap-4">
      <InfoItem
        icon="credit-card-coins"
        title={t("header.pointconvertTitleOne")}
        description={t("header.pointconvertDescriptionOne")}
      />
      <div className="border-border-weaker border-b" />
      <InfoItem
        icon="coins"
        title={t("header.pointconvertTitleTwo")}
        description={t("header.pointconvertDescriptionTwo")}
      />
      <div className="border-border-weaker border-b" />
      <InfoItem
        icon="air-ticket"
        title={t("header.pointconvertTitleThree")}
        description={t("header.pointconvertDescriptionThree")}
      />
    </div>
    <Link
      href="/credit-card-points"
      onClick={close}
      className="bg-primary w-full rounded-lg py-4 text-center font-medium text-white"
    >
      {t("common.convert")}
    </Link>
  </div>
);

const SavedCardsState = ({ savedCards, t, close }) => (
  <div className="flex flex-col gap-4">
    <div className="flex flex-col gap-1">
      <span className="text-xl font-semibold">
        {t("header.savedCardsTitle")}
      </span>
      <span className="text-foreground-muted">
        {t("header.savedCardsDescription")}
      </span>
    </div>
    <div className="scrollbar-default max-h-[300px] overflow-y-auto pr-1">
      <div className="flex flex-col gap-4">
        {savedCards.map((card) => (
          <CardItem key={card.Id} card={card} t={t} close={close} />
        ))}
      </div>
    </div>
    <Link
      href="/account/saved-cards"
      onClick={close}
      className="bg-primary w-full rounded-lg py-4 text-center font-medium text-white"
    >
      {t("common.allCards")}
    </Link>
  </div>
);

const PointConvertContent = ({ savedCards, t, close }) => {
  return savedCards.length > 0 ? (
    <SavedCardsState savedCards={savedCards} t={t} close={close} />
  ) : (
    <EmptyState t={t} close={close} />
  );
};

// Main component
const HeaderPointConvertButton = () => {
  const t = useTranslationDetector();
  const isMobile = useIsMobile();
  const pathname = usePathname();
  const { isLogin } = useAuth();
  const pointConvertModal = useModal("point-convert-modal");
  const locale = useLocale();
  const [isOpen, toggle] = useToggle();
  const [savedCards, setSavedCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasReadNotification, setHasReadNotification] = useState(true);

  const ref = useRef(null);
  const escapePressed = useKeyPressed("Escape");

  useEffect(() => {
    try {
      const hasRead = localStorage.getItem("notification-read") !== null;
      setHasReadNotification(hasRead);
    } catch (error) {
      console.error("localStorage error:", error);
      setHasReadNotification(false);
    }
  }, []);

  const markAsRead = useCallback(() => {
    try {
      localStorage.setItem("notification-read", "true");
      setHasReadNotification(true);
    } catch (error) {
      console.error("localStorage error:", error);
    }
  }, []);

  const getStoredCards = useCallback(async () => {
    setLoading(true);
    try {
      const response = await savedCardService.getSavedCards({
        FillCardDetails: true,
        ForPointConversion: true,
      });
      if (response?.Cards?.length > 0) {
        setSavedCards(response.Cards);
      }
    } catch (error) {
      console.error("Failed to fetch stored cards:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isLogin) {
      getStoredCards();
    }
  }, [isLogin, getStoredCards]);

  const close = useCallback(() => {
    if (isOpen) {
      toggle();
    }
    if (isMobile) {
      pointConvertModal.close();
    }
  }, [isOpen, toggle, isMobile, pointConvertModal]);

  const handleClickOutside = useCallback(() => {
    if (!isMobile && isOpen) {
      close();
    }
  }, [isMobile, isOpen, close]);

  useOnClickOutside(ref, handleClickOutside);

  useEffect(() => {
    if (escapePressed && isOpen) {
      close();
    }
  }, [escapePressed, isOpen, close]);

  const handleButtonClick = useCallback(() => {
    if (isMobile) {
      pointConvertModal.open({
        isBottomSheet: true,
        title: t("common.point"),
      });
    } else {
      toggle();
    }

    if (!hasReadNotification) {
      markAsRead();
    }
  }, [isMobile, pointConvertModal, t, toggle, hasReadNotification, markAsRead]);

  const content = useMemo(
    () => <PointConvertContent savedCards={savedCards} t={t} close={close} />,
    [savedCards, t, close],
  );

  return (
    <div ref={ref} className="header-point-convert-button relative">
      <button
        onClick={handleButtonClick}
        disabled={loading}
        className="md:border-primary-weak text-primary md:bg-primary-weaker relative flex h-[34px] w-[74px] cursor-pointer items-center justify-center gap-[2px] rounded-lg border border-white bg-white/60 px-3 text-sm font-medium disabled:opacity-50 md:h-11 md:w-auto md:px-[14px] md:text-base"
      >
        <FontAwesomeIcon
          icon={byPrefixAndName.fak["turna-logo-icon"]}
          className="text-[18px]"
        />
        <span>{t("common.point")}</span>
        <NotificationBadge show={!hasReadNotification} />
      </button>

      {isMobile ? (
        <Modal id="point-convert-modal">{content}</Modal>
      ) : (
        isOpen && (
          <div
            className={clsx(
              "shadow-xxl absolute top-full mt-2 w-md rounded-lg bg-white p-4",
              locale === "ar" ? "left-0" : "right-0",
            )}
          >
            {content}
          </div>
        )
      )}
    </div>
  );
};

export default HeaderPointConvertButton;
